import type { Facet } from "@/modules/jobAds/client/api/types"
import type {
  GroupedSelectOptions,
  SelectOptions,
  SimpleSelectOptions,
} from "@/modules/uiKitOverrides/SelectFilter/Select"
import type { SearchConfigFilter, SearchConfigFilterOption } from "@/modules/user/api/types"

import { PROTO_STUDY_LEVELS } from "@/modules/jobAds/client/constants"

export type GenerateOptionsFromSearchConfigAndFacetsParams = {
  facets: Facet[]
  filterNameFromFacet: string
  filterNameFromSearchConfig: string
  searchConfig: SearchConfigFilter[]
}

const computeFacetName = (facet: Facet): string => {
  let facetName = facet.name
  const isFacetStudyLevel = facet.parentName === "study_levels"
  const isFacetStudyLevelId = Object.values(PROTO_STUDY_LEVELS).includes(
    Number(facetName) as (typeof PROTO_STUDY_LEVELS)[keyof typeof PROTO_STUDY_LEVELS]
  )

  if (isFacetStudyLevel && !isFacetStudyLevelId) {
    const computedProperty = `STUDY_LEVEL_${facet.name.toUpperCase()}` as keyof typeof PROTO_STUDY_LEVELS

    facetName = `${PROTO_STUDY_LEVELS[computedProperty]}`
  }

  return facetName
}

export const generateOptionsFromSearchConfigAndFacets = (
  params: GenerateOptionsFromSearchConfigAndFacetsParams
): (GroupedSelectOptions | SimpleSelectOptions)[] => {
  const { facets, filterNameFromFacet, filterNameFromSearchConfig, searchConfig } = params

  const filteredFacets = facets.filter(facet => facet.parentName === filterNameFromFacet)

  const filteredSearchConfig = searchConfig.filter(config => config.name === filterNameFromSearchConfig)[0]

  if (!filteredSearchConfig) {
    return []
  }

  const composeSelectOption = (
    ApiResponseOption: SearchConfigFilterOption
  ): { count: number; label: string; value: string } => ({
    count:
      filteredFacets.filter(filteredFacet => computeFacetName(filteredFacet) === ApiResponseOption.name)[0]?.count ?? 0,
    label: ApiResponseOption.label,
    value: ApiResponseOption.name,
  })

  if (typeof filteredSearchConfig.options === "undefined") {
    return []
  }

  return filteredSearchConfig.options.map(option => {
    // Case of grouped option
    if (option.hasOwnProperty("children") && !!option.children) {
      return {
        label: option.label,
        options: option.children.map(subOption => composeSelectOption(subOption)),
      }
    }

    // Case of simple option
    return composeSelectOption(option)
  })
}

export type GetInitialOptionsParams = {
  options: SelectOptions[]
  param?: string | string[]
}

export const getInitialOptions = (params: GetInitialOptionsParams): SimpleSelectOptions[] => {
  const { options, param } = params

  if (!param) return []

  const flattenOptions: SimpleSelectOptions[] = []

  options.forEach(option => {
    if (option.hasOwnProperty("options")) {
      flattenOptions.push(...(option as GroupedSelectOptions).options)
    } else {
      flattenOptions.push(option as SimpleSelectOptions)
    }
  })

  return flattenOptions.filter(option => {
    if (Array.isArray(param)) {
      return param.includes(option.value)
    }
    return param === option.value
  })
}
